import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  Check,
  Clock,
  Code,
  ExternalLink,
  Globe,
  Mail,
  MessageSquare,
  Palette,
  RefreshCw,
  Save,
  Sliders,
} from 'lucide-react';
import { Badge, Button, Card, CodeBlock, CopyInput, Modal, SimpleTagInput, Spinner, useToast } from '@centraoneplus/ui';
import { useAuthStore } from '@stores/authStore';
import { useTenantContextStore } from '@stores/tenantContextStore';
import { useUnsavedChangesStore } from '@stores/unsavedChangesStore';
import {
  errorMessage,
  useChatWidgetConfig,
  useRotateChatWidgetKey,
  useUpdateChatWidgetConfig,
  type ChatWidgetLanguages,
  type ChatWidgetSettings as Settings,
  type PreChatFieldMode,
} from './api';
import { PLATFORM_GUIDES, buildEmbedSnippet, developerEmail, mailtoLink } from './embed';

type TabId = 'appearance' | 'behaviour' | 'websites' | 'install';

const TABS: Array<{ id: TabId; label: string; icon: typeof Palette }> = [
  { id: 'appearance', label: 'Appearance', icon: Palette },
  { id: 'behaviour', label: 'Behaviour', icon: Sliders },
  { id: 'websites', label: 'Websites', icon: Globe },
  { id: 'install', label: 'Install', icon: Code },
];

const ADMIN_ROLES = ['admin', 'owner'];
const DEFAULT_COLOR = '#2563EB';
const PRECHAT_FIELDS: Array<{ id: 'name' | 'email' | 'phone'; label: string }> = [
  { id: 'name', label: 'Name' },
  { id: 'email', label: 'Email' },
  { id: 'phone', label: 'Phone' },
];

interface Draft {
  settings: Settings;
  allowedOrigins: string[];
}

function sameDraft(a: Draft, b: Draft): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export default function ChatWidgetSettings() {
  const toast = useToast();
  const { user } = useAuthStore();
  const { currentTenant } = useTenantContextStore();
  const tenantId = currentTenant?.id || user?.tenantId;
  const isAdmin = ADMIN_ROLES.includes(user?.role ?? '');

  const { data: config, isLoading, error } = useChatWidgetConfig(isAdmin ? tenantId : undefined);
  const updateConfig = useUpdateChatWidgetConfig(tenantId);
  const rotateKey = useRotateChatWidgetKey(tenantId);
  const setIsDirty = useUnsavedChangesStore((s) => s.setIsDirty);

  const [tab, setTab] = useState<TabId>('appearance');
  const [draft, setDraft] = useState<Draft>({ settings: {}, allowedOrigins: [] });
  const [confirmRotate, setConfirmRotate] = useState(false);

  const saved: Draft = useMemo(
    () => ({ settings: config?.settings ?? {}, allowedOrigins: config?.allowedOrigins ?? [] }),
    [config],
  );
  const isDirty = !!config && !sameDraft(draft, saved);

  // Reset the form whenever a fresh copy arrives from the server.
  useEffect(() => {
    if (config) setDraft({ settings: config.settings ?? {}, allowedOrigins: config.allowedOrigins ?? [] });
  }, [config]);

  useEffect(() => {
    setIsDirty(isDirty);
    return () => setIsDirty(false);
  }, [isDirty, setIsDirty]);

  const setSetting = <K extends keyof Settings>(key: K, value: Settings[K]) =>
    setDraft((d) => ({ ...d, settings: { ...d.settings, [key]: value } }));

  const setPreChatField = (id: 'name' | 'email' | 'phone', mode: PreChatFieldMode) =>
    setSetting('preChatFields', { ...draft.settings.preChatFields, [id]: mode });

  const languages = draft.settings.languages ?? {};
  const supportedLanguages = languages.supported ?? [];
  // Built from the list the server sent, so there is never a second list here
  // to drift from the one the settings are validated against.
  const languageOptions = config?.languageOptions ?? [];

  const setLanguages = (change: Partial<ChatWidgetLanguages>) =>
    setSetting('languages', { ...languages, ...change });

  /**
   * Turning a language off must not leave the default pointing at it, or the
   * chatbot would fall back to a language the tenant no longer supports.
   */
  const toggleLanguage = (code: string) => {
    const next = supportedLanguages.includes(code)
      ? supportedLanguages.filter((c) => c !== code)
      : [...supportedLanguages, code];
    const fallback = next.includes(languages.default ?? '') ? languages.default : next[0];
    setLanguages({ supported: next, default: fallback });
  };

  const handleSave = async () => {
    try {
      await updateConfig.mutateAsync({ settings: draft.settings, allowedOrigins: draft.allowedOrigins });
      toast.success('Saved', 'Chat widget settings are live on your websites');
    } catch (err) {
      toast.error('Could not save', errorMessage(err, 'Please check the highlighted settings and try again.'));
    }
  };

  const handleToggleEnabled = async () => {
    if (!config) return;
    try {
      await updateConfig.mutateAsync({ enabled: !config.enabled });
      toast.success(config.enabled ? 'Chat widget turned off' : 'Chat widget turned on');
    } catch (err) {
      toast.error('Could not change the widget', errorMessage(err, 'Please try again.'));
    }
  };

  const handleRotate = async () => {
    try {
      await rotateKey.mutateAsync();
      setConfirmRotate(false);
      toast.success('New key created', 'Update the snippet on your websites - the old one no longer works.');
    } catch (err) {
      toast.error('Could not rotate the key', errorMessage(err, 'Please try again.'));
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-6">
        <Card className="p-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5" />
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Administrators only</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Ask an administrator of your organization to set up the website chat widget.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="h-64 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !config) {
    return (
      <div className="p-6">
        <Card className="p-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {errorMessage(error, 'The chat widget settings could not be loaded. Please try again.')}
          </p>
        </Card>
      </div>
    );
  }

  const snippet = config.embed ? buildEmbedSnippet(config.embed.loaderUrl, config.publicKey, config.embed.apiUrl) : '';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <MessageSquare className="w-5 h-5" /> Chat Widget
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Add the AI chatbot to your own website - no code needed. Changes go live without touching your site.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant={config.enabled ? 'success' : 'default'}>{config.enabled ? 'Live' : 'Off'}</Badge>
          <Button
            variant={config.enabled ? 'outline' : 'primary'}
            onClick={handleToggleEnabled}
            disabled={updateConfig.isPending || isDirty}
            title={isDirty ? 'Save your changes first' : undefined}
          >
            {config.enabled ? 'Turn off' : 'Turn on'}
          </Button>
        </div>
      </div>

      {!config.enabled && config.allowedOrigins.length === 0 && (
        <Card className="p-4 flex items-start gap-3 border-amber-200 bg-amber-50 dark:bg-amber-900/20">
          <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5" />
          <p className="text-sm text-amber-800 dark:text-amber-200">
            Add your website under <strong>Websites</strong> and save, then turn the widget on.
          </p>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700" role="tablist">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === id
                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-6">
          {tab === 'appearance' && (
            <Card className="p-6 space-y-5">
              <Field label="Bot name" hint="Shown in the chat header and used by the AI to introduce itself.">
                <input
                  className="input w-full"
                  maxLength={60}
                  placeholder="e.g. Nova"
                  value={draft.settings.botName ?? ''}
                  onChange={(e) => setSetting('botName', e.target.value)}
                />
              </Field>
              <Field label="Company name" hint="The AI refers to your company by this name.">
                <input
                  className="input w-full"
                  maxLength={100}
                  placeholder="e.g. KeyNova"
                  value={draft.settings.companyName ?? ''}
                  onChange={(e) => setSetting('companyName', e.target.value)}
                />
              </Field>
              <Field label="Brand colour">
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    aria-label="Brand colour"
                    className="w-10 h-10 rounded cursor-pointer"
                    value={draft.settings.primaryColor || DEFAULT_COLOR}
                    onChange={(e) => setSetting('primaryColor', e.target.value)}
                  />
                  <input
                    className="input w-32 text-sm"
                    value={draft.settings.primaryColor || DEFAULT_COLOR}
                    onChange={(e) => setSetting('primaryColor', e.target.value)}
                  />
                </div>
              </Field>
              <Field label="Button position">
                <div className="flex gap-2">
                  {(['bottom-right', 'bottom-left'] as const).map((position) => (
                    <Button
                      key={position}
                      size="sm"
                      variant={(draft.settings.position || 'bottom-right') === position ? 'primary' : 'outline'}
                      onClick={() => setSetting('position', position)}
                    >
                      {position === 'bottom-right' ? 'Bottom right' : 'Bottom left'}
                    </Button>
                  ))}
                </div>
              </Field>
              <Field label="Button label" hint="Optional text next to the chat button.">
                <input
                  className="input w-full"
                  maxLength={40}
                  placeholder="e.g. Chat with us"
                  value={draft.settings.launcherText ?? ''}
                  onChange={(e) => setSetting('launcherText', e.target.value)}
                />
              </Field>
              <Field label="Avatar image URL" hint="Optional. Must start with https://">
                <input
                  className="input w-full"
                  type="url"
                  placeholder="https://your-site.com/avatar.png"
                  value={draft.settings.avatarUrl ?? ''}
                  onChange={(e) => setSetting('avatarUrl', e.target.value || undefined)}
                />
              </Field>
              <Field label="Welcome message" hint="Shown before the visitor sends their first message.">
                <textarea
                  className="input w-full"
                  rows={3}
                  maxLength={500}
                  placeholder="Hi! How can we help you today?"
                  value={draft.settings.welcomeMessage ?? ''}
                  onChange={(e) => setSetting('welcomeMessage', e.target.value)}
                />
              </Field>
            </Card>
          )}

          {tab === 'behaviour' && (
            <Card className="p-6 space-y-5">
              <Field label="Tone of the AI assistant">
                <div className="flex flex-wrap gap-2">
                  {(['friendly', 'professional', 'concise'] as const).map((tone) => (
                    <Button
                      key={tone}
                      size="sm"
                      variant={draft.settings.tone === tone ? 'primary' : 'outline'}
                      onClick={() => setSetting('tone', tone)}
                    >
                      {tone[0].toUpperCase() + tone.slice(1)}
                    </Button>
                  ))}
                </div>
              </Field>
              <Field
                label="Languages"
                hint="The assistant answers each customer in whichever of these they write in. Anything else is answered in the default."
              >
                <div className="space-y-3">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={languages.enabled ?? false}
                      onChange={(e) => setLanguages({ enabled: e.target.checked })}
                    />
                    Answer in the customer&apos;s language
                  </label>

                  {languages.enabled && (
                    <>
                      <div className="flex flex-wrap gap-2">
                        {languageOptions.map((language) => (
                          <Button
                            key={language.code}
                            size="sm"
                            variant={supportedLanguages.includes(language.code) ? 'primary' : 'outline'}
                            onClick={() => toggleLanguage(language.code)}
                            title={language.nativeName}
                          >
                            {language.name}
                          </Button>
                        ))}
                      </div>

                      {supportedLanguages.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                          Choose at least one language, or the assistant carries on as it does today.
                        </p>
                      ) : (
                        <label className="block text-sm">
                          <span className="mr-2">Answer in</span>
                          <select
                            className="input w-48"
                            value={languages.default ?? supportedLanguages[0]}
                            onChange={(e) => setLanguages({ default: e.target.value })}
                          >
                            {supportedLanguages.map((code) => (
                              <option key={code} value={code}>
                                {languageOptions.find((l) => l.code === code)?.name ?? code}
                              </option>
                            ))}
                          </select>
                          <span className="ml-2 text-muted-foreground">
                            when a customer writes in anything else
                          </span>
                        </label>
                      )}
                    </>
                  )}
                </div>
              </Field>
              <Field label="Open automatically" hint="Open the chat after a visitor has been on the page this long.">
                <select
                  className="input w-48"
                  value={draft.settings.autoOpenDelaySeconds ?? 0}
                  onChange={(e) => setSetting('autoOpenDelaySeconds', Number(e.target.value))}
                >
                  <option value={0}>Never</option>
                  <option value={5}>After 5 seconds</option>
                  <option value={15}>After 15 seconds</option>
                  <option value={30}>After 30 seconds</option>
                  <option value={60}>After 1 minute</option>
                </select>
              </Field>
              <Field label="Ask before the chat starts" hint="Hidden fields are not asked. If every field is hidden, the chat starts straight away.">
                <div className="space-y-2">
                  {PRECHAT_FIELDS.map(({ id, label }) => (
                    <div key={id} className="flex items-center justify-between max-w-sm">
                      <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                      <select
                        className="input w-36 text-sm"
                        aria-label={`${label} field`}
                        value={draft.settings.preChatFields?.[id] ?? 'hidden'}
                        onChange={(e) => setPreChatField(id, e.target.value as PreChatFieldMode)}
                      >
                        <option value="required">Required</option>
                        <option value="optional">Optional</option>
                        <option value="hidden">Don&apos;t ask</option>
                      </select>
                    </div>
                  ))}
                </div>
              </Field>
              <Field label="Offline message" hint="Shown when no agent is available.">
                <textarea
                  className="input w-full"
                  rows={2}
                  maxLength={500}
                  placeholder="We are currently offline. Leave us a message and we'll get back to you."
                  value={draft.settings.offlineMessage ?? ''}
                  onChange={(e) => setSetting('offlineMessage', e.target.value)}
                />
              </Field>
            </Card>
          )}

          {tab === 'websites' && (
            <Card className="p-6 space-y-4">
              <Field
                label="Allowed websites"
                hint="The chat only works on these sites. Type a domain and press Enter - e.g. keynova.ai, or *.keynova.ai for all subdomains."
              >
                <SimpleTagInput
                  value={draft.allowedOrigins}
                  onChange={(allowedOrigins) => setDraft((d) => ({ ...d, allowedOrigins }))}
                  placeholder="keynova.ai"
                  maxTags={20}
                />
              </Field>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Websites must use https. http is only accepted for localhost testing.
              </p>
            </Card>
          )}

          {tab === 'install' && (
            <div className="space-y-6">
              <Card className="p-6 space-y-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">1. Add this line to your website</h3>
                {snippet ? (
                  <CodeBlock code={snippet} language="html" showCopyButton />
                ) : (
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    The widget address is not configured on this environment yet. Contact your CentraOne team.
                  </p>
                )}
                {snippet && (
                  <div className="flex flex-wrap gap-2">
                    <a
                      className="inline-flex items-center gap-2 text-sm text-blue-600 hover:underline"
                      href={mailtoLink(developerEmail(snippet, config.embed?.hostedChatUrl))}
                    >
                      <Mail className="w-4 h-4" /> Email the instructions to your developer
                    </a>
                  </div>
                )}
              </Card>

              {snippet && (
                <Card className="p-6 space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Step-by-step for your platform</h3>
                  {PLATFORM_GUIDES.map((guide) => (
                    <details key={guide.id} className="group border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                      <summary className="cursor-pointer text-sm font-medium text-gray-800 dark:text-gray-200">
                        {guide.name}
                      </summary>
                      <ol className="list-decimal ml-5 mt-2 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                        {guide.steps.map((step) => (
                          <li key={step}>{step}</li>
                        ))}
                      </ol>
                    </details>
                  ))}
                </Card>
              )}

              {config.embed && (
                <Card className="p-6 space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">2. Or share a chat link</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    A full-page chat for emails, social profiles or a mobile app - no website changes needed.
                  </p>
                  <CopyInput value={config.embed.hostedChatUrl} readOnly />
                  <a
                    className="inline-flex items-center gap-2 text-sm text-blue-600 hover:underline"
                    href={config.embed.hostedChatUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="w-4 h-4" /> Open the chat page
                  </a>
                </Card>
              )}

              <Card className="p-6 space-y-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Install status</h3>
                {config.lastSeenAt ? (
                  <p className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" /> Last seen on{' '}
                    <strong>{config.lastSeenOrigin}</strong> at {new Date(config.lastSeenAt).toLocaleString()}
                  </p>
                ) : (
                  <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                    <Clock className="w-4 h-4" /> Not seen on any website yet.
                  </p>
                )}
                <div className="pt-2 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between gap-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Widget key <code className="font-mono">{config.publicKey}</code>. Replace it if it was used on a site
                    you did not authorise.
                  </p>
                  <Button size="sm" variant="outline" onClick={() => setConfirmRotate(true)}>
                    <RefreshCw className="w-4 h-4 mr-1" /> Replace key
                  </Button>
                </div>
              </Card>
            </div>
          )}

          {tab !== 'install' && (
            <div className="flex justify-end gap-2">
              <Button variant="outline" disabled={!isDirty} onClick={() => setDraft(saved)}>
                Discard changes
              </Button>
              <Button onClick={handleSave} disabled={!isDirty || updateConfig.isPending}>
                <Save className="w-4 h-4 mr-1" />
                {updateConfig.isPending ? 'Saving...' : 'Save changes'}
              </Button>
            </div>
          )}
        </div>

        <div className="lg:col-span-2">
          <WidgetPreview settings={draft.settings} />
        </div>
      </div>

      <Modal isOpen={confirmRotate} onClose={() => setConfirmRotate(false)} title="Replace the widget key?">
        <div className="space-y-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            The chat stops working on every website that uses the current snippet until you paste the new one.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfirmRotate(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleRotate} disabled={rotateKey.isPending}>
              {rotateKey.isPending ? 'Replacing...' : 'Replace key'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
      {children}
      {hint && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

/** A static rendering of how the widget will look with the current (unsaved) settings. */
export function WidgetPreview({ settings }: { settings: Settings }) {
  const color = settings.primaryColor || DEFAULT_COLOR;
  const left = settings.position === 'bottom-left';
  return (
    <Card className="p-4 sticky top-4">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3">Preview</p>
      <div className="relative h-[460px] rounded-lg bg-gray-100 dark:bg-gray-800 overflow-hidden" data-testid="widget-preview">
        <div
          className={`absolute bottom-20 w-[260px] max-w-[calc(100%-2rem)] rounded-xl bg-white shadow-xl overflow-hidden ${left ? 'left-4' : 'right-4'}`}
          // The widget is white on the customer's site whatever theme this
          // console is in, so the preview does not follow the dark surface.
          style={{ backgroundColor: '#fff' }}
        >
          <div className="px-4 py-3 text-white flex items-center gap-2" style={{ backgroundColor: color }}>
            {settings.avatarUrl?.startsWith('https://') ? (
              <img src={settings.avatarUrl} alt="" className="w-7 h-7 rounded-full bg-white/20 object-cover" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-white/20" />
            )}
            <span className="text-sm font-semibold truncate">{settings.botName || 'Support'}</span>
          </div>
          <div className="p-4 h-40 text-center text-xs text-gray-500 flex items-center justify-center whitespace-pre-line" style={{ color: '#6b7280' }}>
            {settings.welcomeMessage || "Send us a message and we'll get back to you as soon as possible."}
          </div>
          <div className="border-t border-gray-100 px-3 py-2 text-xs text-gray-400" style={{ borderColor: '#f3f4f6', color: '#9ca3af' }}>Type a message...</div>
        </div>
        <div className={`absolute bottom-4 flex items-center gap-2 ${left ? 'left-4 flex-row-reverse' : 'right-4'}`}>
          {settings.launcherText && (
            <span className="bg-white text-xs text-gray-700 rounded-full px-3 py-1 shadow" style={{ backgroundColor: '#fff', color: '#374151' }}>{settings.launcherText}</span>
          )}
          <div className="w-12 h-12 rounded-full shadow-lg flex items-center justify-center" style={{ backgroundColor: color }}>
            <MessageSquare className="w-5 h-5 text-white" />
          </div>
        </div>
      </div>
    </Card>
  );
}
